import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, FoldVertical, ListTree, Loader2, Search, UnfoldVertical, X, Check, Minus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import type { ScopeNode } from "../types";
import { ancestorScopeIds, collectScopeIdsUnder } from "../utils";
import { SCOPE_TYPES } from "../constants";
import { childTypeSummary, scopeTriggerSummary } from "../utils";
 
interface ScopeTreeFilterProps {
  data: ScopeNode[];
  value: string[];
  onChange: (ids: string[]) => void;
  loading?: boolean;
  triggerLabel?: string;
  placeholder?: string; 
  "aria-label"?: string;
  /** "pill" matches the compact toolbar filter dropdowns border (grey, indigo on hover, no blue when selected). */
  variant?: "default" | "pill";
}

type CheckState = "checked" | "indeterminate" | "unchecked";

function nodeCheckState(node: ScopeNode, selected: Set<string>): CheckState {
  if (!node.children?.length) {
    return selected.has(node.id) ? "checked" : "unchecked";
  }

  let hasChecked = false;
  let hasUnchecked = false;

  for (const child of node.children) {
    const childState = nodeCheckState(child, selected);
    if (childState === "indeterminate") {
      return "indeterminate";
    }
    if (childState === "checked") {
      hasChecked = true;
    } else {
      hasUnchecked = true;
    }
    if (hasChecked && hasUnchecked) {
      return "indeterminate";
    }
  }

  if (hasChecked && !hasUnchecked) {
    return "checked";
  }
  return "unchecked";
}

function syncParentSelections(nodes: ScopeNode[], selectedSet: Set<string>): void {
  for (const node of nodes) {
    if (node.children?.length) {
      syncParentSelections(node.children, selectedSet);
      const state = nodeCheckState(node, selectedSet);
      if (state === "checked") {
        selectedSet.add(node.id);
      } else {
        selectedSet.delete(node.id);
      }
    }
  }
}

function sortByTypeOrder(nodes: ScopeNode[]): ScopeNode[] {
  return [...nodes].sort((a, b) => {
    const oa = SCOPE_TYPES[a.type]?.order ?? 0;
    const ob = SCOPE_TYPES[b.type]?.order ?? 0;
    if (oa !== ob) return oa - ob;
    return a.name.localeCompare(b.name);
  });
}

function TriStateCheckbox({ state }: { state: CheckState }) {
  if (state === "unchecked") {
    return <span className="size-4 shrink-0 rounded-[4px] border border-border" aria-hidden />;
  }
  return (
    <span className="flex size-4 shrink-0 items-center justify-center rounded-[4px] bg-blue-600 text-white dark:bg-blue-500" aria-hidden>
      {state === "checked" ? <Check className="size-3" strokeWidth={3} /> : <Minus className="size-3" strokeWidth={3} />}
    </span>
  );
}

function ScopeTreeRow({
  node,
  depth,
  selected,
  expanded,
  onToggleExpand,
  onToggleSelect,
  highlight,
}: {
  node: ScopeNode;
  depth: number;
  selected: Set<string>;
  expanded: Set<string>;
  onToggleExpand: (id: string) => void;
  onToggleSelect: (node: ScopeNode) => void;
  highlight: string;
}) {
  const hasChildren = !!node.children?.length;
  const isOpen = hasChildren && expanded.has(node.id);
  const state = nodeCheckState(node, selected);
  const meta = SCOPE_TYPES[node.type];
  const Icon = meta?.icon;
  const summary = !isOpen ? childTypeSummary(node) : [];

  return (
    <div>
      <div
        role="treeitem"
        aria-expanded={hasChildren ? isOpen : undefined}
        aria-selected={state === "checked"}
        className="group flex h-9 items-center gap-2 rounded-md px-2 text-sm hover:bg-accent/60"
        style={{ paddingLeft: 8 + depth * 16 }}
      >
        <button
          type="button"
          aria-label={hasChildren ? (isOpen ? "Collapse" : "Expand") : undefined}
          tabIndex={hasChildren ? 0 : -1}
          onClick={() => hasChildren && onToggleExpand(node.id)}
          className={`flex h-5 w-4 shrink-0 items-center justify-center rounded text-muted-foreground ${hasChildren ? "hover:text-foreground" : "invisible"}`}
        >
          <ChevronRight className={`size-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`} />
        </button>

        <button
          type="button"
          onClick={() => onToggleSelect(node)}
          aria-label={`Select ${node.name}`}
          className="flex size-4 shrink-0 items-center justify-center rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        >
          <TriStateCheckbox state={state} />
        </button>

        {Icon && (
          <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground" aria-hidden>
            <Icon className="size-3.5" />
          </span>
        )}

        {meta?.label && (
          <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wider ${meta.chipClassName}`}>
            {meta.label}
          </span>
        )}

        <span className="min-w-0 flex-1 truncate" title={node.name}>
          {node.name}
        </span>

        {summary.length > 0 && (
          <span className="flex shrink-0 items-center gap-2 text-[11px] text-muted-foreground">
            {summary.map(({ type, count }) => {
              const ChildIcon = SCOPE_TYPES[type]?.icon;
              return (
                <span key={type} className="inline-flex items-center gap-0.5">
                  {ChildIcon && <ChildIcon className="size-3" />}
                  <span className="tabular-nums">{count}</span>
                </span>
              );
            })}
          </span>
        )}
      </div>
      {hasChildren && isOpen && (
        <div>
          {sortByTypeOrder(node.children!)
            .filter((c) => !highlight || nodeOrDescendantMatches(c, highlight))
            .map((child) => (
              <ScopeTreeRow
                key={child.id}
                node={child}
                depth={depth + 1}
                selected={selected}
                expanded={expanded}
                onToggleExpand={onToggleExpand}
                onToggleSelect={onToggleSelect}
                highlight={highlight}
              />
            ))}
        </div>
      )}
    </div>
  );
}

/** Trigger + popover scope picker for the org/department/division/
 * application/asset "Group" filter — matches the source dashboard's
 * ScopeTreeFilter: a fixed-width trigger button showing a type-aware
 * selection summary, opening a popover with a searchable, colour-coded
 * tree (icon + type chip + child-count breakdown per row, tri-state
 * checkboxes) and a "Clear all" footer. */
export function ScopeTreeFilter({
  data,
  value,
  onChange,
  loading = false,
  triggerLabel = "Group",
  placeholder = "All groups",
  variant = "default",
}: ScopeTreeFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // null = untouched, so expansion follows the auto-seeded selection's path.
  // The first manual expand/collapse materializes the set and takes over, which
  // is why this is derived rather than an effect seeding state on data arrival.
  const [expandedOverride, setExpandedOverride] = useState<Set<string> | null>(null);
  const selected = useMemo(() => {
    const set = new Set(value);
    if (data.length > 0 && set.size > 0) {
      syncParentSelections(data, set);
    }
    return set;
  }, [data, value]);

  const effectiveValue = useMemo(() => Array.from(selected), [selected]);

  const expanded = useMemo(
    () => expandedOverride ?? new Set(ancestorScopeIds(data, effectiveValue)),
    [expandedOverride, data, effectiveValue],
  );

  const summary = useMemo(() => scopeTriggerSummary(data, effectiveValue, placeholder), [data, effectiveValue, placeholder]);
  const isActive = effectiveValue.length > 0;

  const toggle = (node: ScopeNode) => {
    const currentState = nodeCheckState(node, selected);
    const shouldSelect = currentState !== "checked";
    const idsUnder = collectScopeIdsUnder(data, node.id);
    const nextSelected = new Set(selected);

    if (shouldSelect) {
      for (const id of idsUnder) {
        nextSelected.add(id);
      }
    } else {
      for (const id of idsUnder) {
        nextSelected.delete(id);
      }
    }

    syncParentSelections(data, nextSelected);
    onChange(Array.from(nextSelected));
  };

  const toggleExpand = (id: string) => {
    setExpandedOverride((prev) => {
      const next = new Set(prev ?? expanded);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const collectExpandableIds = (nodes: ScopeNode[]): string[] => {
    const ids: string[] = [];
    const walk = (n: ScopeNode[]) => {
      for (const node of n) {
        if (node.children?.length) {
          ids.push(node.id);
          walk(node.children);
        }
      }
    };
    walk(nodes);
    return ids;
  };
  const expandAll = () => setExpandedOverride(new Set(collectExpandableIds(data)));
  const collapseAll = () => setExpandedOverride(new Set());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Group filter"
          data-active={isActive || undefined}
          className={`inline-flex h-10 min-w-[22rem] max-w-[22rem] items-center gap-2 rounded-md border bg-card px-3 text-sm whitespace-nowrap transition-colors outline-none ${
            variant === "pill"
              ? "border-border hover:border-indigo-400/60 focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500/20"
              : `${isActive ? "border-blue-600 dark:border-blue-500" : "border-border"} hover:border-blue-500/60 dark:hover:border-blue-400/60`
          }`}
        >
          <span className="flex size-5 shrink-0 items-center justify-center text-foreground">
            <ListTree className="size-4" />
          </span>
          <span className="font-semibold">{triggerLabel}</span>
          <span aria-hidden className="text-muted-foreground">
            ·
          </span>
          <span className={`min-w-0 flex-1 truncate text-left ${isActive ? "" : "text-muted-foreground"}`} title={summary}>
            {summary}
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[36rem] p-0">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          {query && (
            <button type="button" onClick={() => setQuery("")} aria-label="Clear search" className="text-muted-foreground hover:text-foreground">
              <X className="size-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={expandAll}
            aria-label="Expand all"
            title="Expand all"
            className="rounded p-1 text-muted-foreground hover:bg-accent/60 hover:text-foreground"
          >
            <UnfoldVertical className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={collapseAll}
            aria-label="Collapse all"
            title="Collapse all"
            className="rounded p-1 text-muted-foreground hover:bg-accent/60 hover:text-foreground"
          >
            <FoldVertical className="size-3.5" />
          </button>
        </div>

        <div className="max-h-80 overflow-y-auto p-1">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground px-2 py-3">
              <Loader2 className="size-3.5 animate-spin" />
              Loading organization...
            </div>
          )}
          {!loading && data.length === 0 && (
            <p className="text-xs text-muted-foreground px-2 py-3">No organization hierarchy available.</p>
          )}
          {!loading &&
            sortByTypeOrder(data)
              .filter((node) => !query.trim() || nodeOrDescendantMatches(node, query.trim().toLowerCase()))
              .map((node) => (
                <ScopeTreeRow
                  key={node.id}
                  node={node}
                  depth={0}
                  selected={selected}
                  expanded={query.trim() ? new Set(collectExpandableIds([node])) : expanded}
                  onToggleExpand={toggleExpand}
                  onToggleSelect={toggle}
                  highlight={query.trim().toLowerCase()}
                />
              ))}
        </div>
        <div className="flex items-center justify-start border-t border-border px-3 py-2">
          <button
            type="button"
            onClick={() => onChange([])}
            disabled={effectiveValue.length === 0}
            className="text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Clear all
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function nodeOrDescendantMatches(node: ScopeNode, query: string): boolean {
  if (node.name.toLowerCase().includes(query)) return true;
  return node.children?.some((c) => nodeOrDescendantMatches(c, query)) ?? false;
}
